import { getChatApi } from "@/chatApi";
import { getNamespace } from "@/namespace";

/** Storage utilities for localStorage and sessionStorage */

/** Check if storage is available in the current environment */
function isStorageAvailable(type: "localStorage" | "sessionStorage"): boolean {
  try {
    const storage = window[type];
    const testKey = "__storage_test__";
    storage.setItem(testKey, "test");
    storage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
}

/** Base storage interface */
interface StorageInterface {
  get<T = string>(key: string): T | null;
  set<T = string>(key: string, value: T): boolean;
  remove(key: string): boolean;
  clear(): boolean;
  getJSON<T = any>(key: string): T | null;
  setJSON<T = any>(key: string, value: T): boolean;
  has(key: string): boolean;
  getAllKeys(): string[];
}

/** Create storage wrapper with error handling */
function createStorageWrapper(
  type: "localStorage" | "sessionStorage",
): StorageInterface {
  const isAvailable = isStorageAvailable(type);
  const storage = isAvailable ? window[type] : null;

  return {
    /** Get a string value from storage */
    get<T = string>(key: string): T | null {
      if (!storage) return null;
      try {
        const item = storage.getItem(key);
        return item as T;
      } catch (error) {
        console.warn(`Failed to get ${type} item "${key}":`, error);
        return null;
      }
    },

    /** Set a string value in storage */
    set<T = string>(key: string, value: T): boolean {
      if (!storage) return false;
      try {
        storage.setItem(key, String(value));
        return true;
      } catch (error) {
        console.warn(`Failed to set ${type} item "${key}":`, error);
        return false;
      }
    },

    /** Remove an item from storage */
    remove(key: string): boolean {
      if (!storage) return false;
      try {
        storage.removeItem(key);
        return true;
      } catch (error) {
        console.warn(`Failed to remove ${type} item "${key}":`, error);
        return false;
      }
    },

    /** Clear all items from storage */
    clear(): boolean {
      if (!storage) return false;
      try {
        storage.clear();
        return true;
      } catch (error) {
        console.warn(`Failed to clear ${type}:`, error);
        return false;
      }
    },

    /** Get a JSON-parsed value from storage */
    getJSON<T = any>(key: string): T | null {
      if (!storage) return null;
      try {
        const item = storage.getItem(key);
        return item ? JSON.parse(item) : null;
      } catch (error) {
        console.warn(`Failed to get JSON ${type} item "${key}":`, error);
        return null;
      }
    },

    /** Set a JSON-stringified value in storage */
    setJSON<T = any>(key: string, value: T): boolean {
      if (!storage) return false;
      try {
        storage.setItem(key, JSON.stringify(value));
        return true;
      } catch (error) {
        console.warn(`Failed to set JSON ${type} item "${key}":`, error);
        return false;
      }
    },

    /** Check if a key exists in storage */
    has(key: string): boolean {
      if (!storage) return false;
      try {
        return storage.getItem(key) !== null;
      } catch (error) {
        console.warn(`Failed to check ${type} item "${key}":`, error);
        return false;
      }
    },

    /** Get all keys from storage */
    getAllKeys(): string[] {
      if (!storage) return [];
      try {
        return Object.keys(storage);
      } catch (error) {
        console.warn(`Failed to get ${type} keys:`, error);
        return [];
      }
    },
  };
}

/** localStorage utilities */
export const localStorage = createStorageWrapper("localStorage");

/** sessionStorage utilities */
export const sessionStorage = createStorageWrapper("sessionStorage");

/** Check if localStorage is available */
export const isLocalStorageAvailable = (): boolean =>
  isStorageAvailable("localStorage");

/** Check if sessionStorage is available */
export const isSessionStorageAvailable = (): boolean =>
  isStorageAvailable("sessionStorage");

/** Visitor id and conversation id. localStorage when it works; otherwise this tab. */
const durableStorage: StorageInterface = isLocalStorageAvailable()
  ? localStorage
  : sessionStorage;

/** Where the conversation ID lives. localStorage keeps the thread when a chat
 * link opens a new tab; some embedded contexts block it, and a per-tab
 * conversation is still better than losing it on every page view. */
const conversationStorage: StorageInterface = durableStorage;

function getStorageKeyTemplates() {
  const ns = getNamespace();
  return {
    USER_ID: `${ns}_user_id`,
    CONVERSATION_ID: `${ns}_conversation_id`,
  };
}

/** Generate site-specific storage key */
export function getSiteSpecificKey(
  keyTemplate: string,
  siteId?: string,
): string {
  const currentSiteId = siteId || getChatApi().chat.id;
  if (!currentSiteId) {
    console.warn(
      "No site ID available for storage key generation, using template as-is",
    );
    return keyTemplate;
  }
  return `${keyTemplate}_${currentSiteId}`;
}

/** Get all storage keys for a specific site */
export function getSiteStorageKeys(siteId?: string) {
  const currentSiteId = siteId || getChatApi().chat.id;
  if (!currentSiteId) {
    console.warn("No site ID available for storage keys generation");
    return getStorageKeyTemplates();
  }

  const templates = getStorageKeyTemplates();
  return {
    USER_ID: getSiteSpecificKey(templates.USER_ID, currentSiteId),
    CONVERSATION_ID: getSiteSpecificKey(
      templates.CONVERSATION_ID,
      currentSiteId,
    ),
  };
}

/** Helper functions for common storage operations */
export const storage = {
  /** Get user ID. Falls back to sessionStorage when localStorage is blocked. */
  getUserId: (siteId?: string): string | null => {
    const key = getSiteSpecificKey(getStorageKeyTemplates().USER_ID, siteId);
    return durableStorage.get(key);
  },

  /** Set user ID in the same store getUserId reads. */
  setUserId: (userId: string, siteId?: string): boolean => {
    const key = getSiteSpecificKey(getStorageKeyTemplates().USER_ID, siteId);
    return durableStorage.set(key, userId);
  },

  /** Get conversation ID, migrating a value left behind by a build that stored
   * it per-tab. The session copy is only dropped once the move succeeds. */
  getConversationId: (siteId?: string): string | null => {
    const key = getSiteSpecificKey(
      getStorageKeyTemplates().CONVERSATION_ID,
      siteId,
    );
    const stored = conversationStorage.get(key);
    if (stored || conversationStorage === sessionStorage) {
      return stored;
    }

    const fromSession = sessionStorage.get(key);
    if (fromSession && conversationStorage.set(key, fromSession)) {
      sessionStorage.remove(key);
    }
    return fromSession;
  },

  /** Set conversation ID (an empty id clears it) */
  setConversationId: (conversationId: string, siteId?: string): boolean => {
    const key = getSiteSpecificKey(
      getStorageKeyTemplates().CONVERSATION_ID,
      siteId,
    );
    if (conversationStorage !== sessionStorage) {
      sessionStorage.remove(key);
    }
    if (!conversationId) {
      return conversationStorage.remove(key);
    }
    return conversationStorage.set(key, conversationId);
  },

  /** Clear namespace-prefixed storage for a specific site */
  clearSite: (siteId?: string): void => {
    const keys = getSiteStorageKeys(siteId);
    Object.values(keys).forEach((key) => {
      localStorage.remove(key);
      sessionStorage.remove(key);
    });
  },

  /** Clear namespace-prefixed storage across all sites */
  clearAll: (): void => {
    const prefix = `${getNamespace()}_`;
    const allKeys = [
      ...localStorage.getAllKeys(),
      ...sessionStorage.getAllKeys(),
    ];
    const namespacedKeys = allKeys.filter((key) => key.startsWith(prefix));

    namespacedKeys.forEach((key) => {
      localStorage.remove(key);
      sessionStorage.remove(key);
    });
  },
};
